import React, { useEffect, useRef, useState } from 'react'
import { Stack, Typography, MenuItem, Box, Button, Menu, Divider, useTheme } from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

export interface DateRangePreset {
  id: string
  label: string
}

interface DateRangeSelectorProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
  onStartDateChange: (date: Dayjs | null) => void
  onEndDateChange: (date: Dayjs | null) => void
  displayText: string
  onDateRangeChange: (range: string) => void
  selectedDateRange: string
  /** Extra presets rendered after "This Year" and before "Custom Range" */
  additionalPresets?: DateRangePreset[]
  /** When provided, replaces the built-in preset list entirely. Custom Range is always appended. */
  presets?: DateRangePreset[]
  size?: 'small' | 'medium'
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  displayText,
  onDateRangeChange,
  selectedDateRange,
  additionalPresets,
  presets,
  size,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const keepMenuOpenForCustomItemRef = useRef(false)
  const pointerDownInPickerRef = useRef(false)
  const theme = useTheme()
  const isMobile = useIsMobile()

  const datePickerTextFieldProps = {
    size: 'small' as const,
    fullWidth: true,
    sx: {
      '& .MuiInputBase-input': {
        fontSize: isMobile ? '0.8rem' : 'inherit',
        py: isMobile ? 1 : 'inherit',
      },
    },
  }

  // Portaled so the calendar is not inside the menu scroll (which forced scrolling to see
  // the start field's calendar). pointerDownInPickerRef + handleMenuClose keeps the menu open.
  const datePickerPopperProps = {
    sx: { zIndex: theme.zIndex.modal + 2 },
  }

  // Always use the desktop (popper) picker so start and end both open the same way.
  // Default '(pointer: fine)' can pick MobileDatePicker for touch, which is not the popper UX.
  const datePickerModeProps = { desktopModeMediaQuery: '@media (min-width: 0px)' } as const

  useEffect(() => {
    if (!open) {
      return undefined
    }
    const onPointerDown = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null
      if (!el) {
        return
      }
      pointerDownInPickerRef.current = Boolean(
        el.closest?.('.MuiPickersPopper-root, [class*="MuiDateCalendar"]'),
      )
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [open])

  const closeMenu = () => {
    pointerDownInPickerRef.current = false
    setAnchorEl(null)
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePresetItemClick = (range: string) => {
    onDateRangeChange(range)
    closeMenu()
  }

  // MUI's Menu onClose `reason` typing is narrower than runtime (MenuItem uses menuItemClick).
  const handleMenuClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown' | 'tabKeyDown' | 'menuItemClick' | (string & {}),
  ) => {
    if (keepMenuOpenForCustomItemRef.current && reason === 'menuItemClick') {
      keepMenuOpenForCustomItemRef.current = false
      return
    }
    if (reason === 'backdropClick' && pointerDownInPickerRef.current) {
      pointerDownInPickerRef.current = false
      return
    }
    closeMenu()
  }

  const handleCustomRangeItemClick = () => {
    keepMenuOpenForCustomItemRef.current = true
    onDateRangeChange('custom')
  }

  const buttonSize = size ?? (isMobile ? 'small' : 'medium')

  return (
    <Box>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        startIcon={<CalendarTodayIcon />}
        variant="outlined"
        size={buttonSize}
        sx={{ 
          borderRadius: 2,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          minWidth: buttonSize === 'small' ? 'auto' : '200px',
          minHeight: buttonSize === 'small' ? 32 : undefined,
          px: buttonSize === 'small' ? 1.25 : 2,
          fontSize: buttonSize === 'small' ? '0.8125rem' : 'inherit'
        }}
      >
        {displayText}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          sx: {
            maxHeight: 'none',
            overflow: 'visible',
          },
        }}
        PaperProps={{
          elevation: 3,
          sx: { 
            width: isMobile ? 280 : 320,
            maxWidth: '95vw',
            borderRadius: 2,
            mt: 1,
            overflow: 'visible',
          }
        }}
      >
        <Typography 
          variant="subtitle2" 
          sx={{ 
            px: 2, 
            py: 1, 
            color: theme.palette.text.secondary,
            fontWeight: 500
          }}
        >
          Select Date Range
        </Typography>
        <Divider />
        {(presets ?? [
          { id: 'this_month', label: 'This Month' },
          { id: 'last_month', label: 'Last Month' },
          { id: 'two_months_ago', label: '2 Months Ago' },
          { id: 'three_months_ago', label: '3 Months Ago' },
          { id: 'four_months_ago', label: '4 Months Ago' },
          { id: 'this_year', label: 'This Year' },
          ...(additionalPresets ?? []),
        ]).map(preset => (
          <MenuItem
            key={preset.id}
            onClick={() => handlePresetItemClick(preset.id)}
            selected={selectedDateRange === preset.id}
          >
            {preset.label}
          </MenuItem>
        ))}
        <MenuItem
          onClick={handleCustomRangeItemClick}
          selected={selectedDateRange === 'custom'}
        >
          Custom Range
        </MenuItem>

        {selectedDateRange === 'custom' && (
          <Box sx={{ px: 2, pb: 2, width: 1, boxSizing: 'border-box' }}>
            <Divider sx={{ my: 1 }} />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack
                direction="column"
                spacing={1.5}
                useFlexGap
                sx={{ mt: 1, width: 1, '& .MuiTextField-root': { width: '100%' } }}
              >
                <DatePicker
                  {...datePickerModeProps}
                  label="Start Date"
                  value={startDate}
                  onChange={onStartDateChange}
                  slotProps={{
                    textField: datePickerTextFieldProps,
                    popper: datePickerPopperProps,
                  }}
                />
                <DatePicker
                  {...datePickerModeProps}
                  label="End Date"
                  value={endDate}
                  onChange={onEndDateChange}
                  slotProps={{
                    textField: datePickerTextFieldProps,
                    popper: datePickerPopperProps,
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Box>
        )}
      </Menu>
    </Box>
  )
}

export default DateRangeSelector
